import type { TravelersState } from "@/lib/cart/types";

import { paxFor, sumPax, type PaxCategory } from "./_capacity";

/** Apply a +/- step to one category's count for the booking-widget picker.
 *
 *  The picker is freely editable; it clamps only on hard limits:
 *   - a count can never go below 0,
 *   - total head-count can never exceed the picker cap (the experience's total
 *     capacity — per-unit/group categories count by their seat fan-out),
 *   - the last remaining traveller can't be removed (party stays >= 1),
 *   - a rental can't go past the units its resource has left on the date
 *     (`unitCaps`, keyed by category id; absent = no known limit).
 *
 *  Per-category and per-package MINIMUMS are deliberately NOT enforced here.
 *  A minimum is a package-card concern: the card shows "This package requires a
 *  minimum of N participants." and disables Book until it's met. Enforcing the
 *  minimum as a picker floor (the previous behaviour) froze a category whose
 *  default count sat below its minimum — every +1 step landed below the floor
 *  and was rejected, so the user could never reach the minimum. */
export function reduceTravelerUpdate(args: {
  travelers: TravelersState;
  categories: ReadonlyArray<PaxCategory>;
  categoryId: string;
  increment: number;
  pickerCap: number | null;
  unitCaps?: Readonly<Record<string, number>>;
}): TravelersState {
  const { travelers, categories, categoryId, increment, pickerCap } = args;

  const prevCount = travelers[categoryId] ?? 0;
  const newValue = prevCount + increment;
  if (newValue < 0 || isOverUnitCap(args.unitCaps, categoryId, newValue)) {
    return travelers;
  }

  const cat = categories.find((c) => c.id === categoryId);
  // Capacity is measured in heads, not category-rows; one "Couple" increment
  // consumes `unitCapacity` seats. Project the proposed change through `paxFor`.
  const paxDelta = cat
    ? paxFor(cat, newValue) - paxFor(cat, prevCount)
    : increment;
  const totalTravelers = sumPax(categories, travelers);
  if (pickerCap !== null && totalTravelers + paxDelta > pickerCap) {
    return travelers;
  }

  const next = { ...travelers, [categoryId]: newValue };
  // A booking needs at least one traveller. Any single category may drop to 0,
  // but block the decrement that would empty the whole party.
  if (increment < 0 && partySize(categories, next) < 1) return travelers;

  return next;
}

/** Only a step up can pass a rental's cap; a step down from above it (the
 *  date shrank) always lands closer to it. */
function isOverUnitCap(
  unitCaps: Readonly<Record<string, number>> | undefined,
  categoryId: string,
  newValue: number,
): boolean {
  const cap = unitCaps?.[categoryId];
  return cap !== undefined && newValue > cap;
}

/** Would a +1 step on this category be accepted? Answered by asking the reducer
 *  itself (a rejected step returns the SAME state object), so the "+" button's
 *  disabled state can never drift from what the reducer actually allows.
 *
 *  Needed because "cap reached" is not the same as "cap reachable": with a cap
 *  of 15, 7 Couples is 14 heads — under the cap, yet the next Couple is 2 heads
 *  and overshoots to 16. Without this the "+" stayed enabled and silently did
 *  nothing. */
export function canIncrementCategory(args: {
  travelers: TravelersState;
  categories: ReadonlyArray<PaxCategory>;
  categoryId: string;
  pickerCap: number | null;
  unitCaps?: Readonly<Record<string, number>>;
}): boolean {
  return reduceTravelerUpdate({ ...args, increment: 1 }) !== args.travelers;
}

/** The fewest a row's "−" may go to. Only the step that would empty the whole
 *  party is refused; a rental carries nobody (`paxFor` = 0), so taking one
 *  away never empties it. */
export function decrementFloor(args: {
  cat: PaxCategory;
  categories: ReadonlyArray<PaxCategory>;
  travelers: TravelersState;
}): number {
  const { cat, categories, travelers } = args;
  const count = travelers[cat.id] ?? 0;
  if (count === 0) return 0;
  const after = { ...travelers, [cat.id]: count - 1 };
  return partySize(categories, after) < 1 ? count : 0;
}

/** What "the party" counts: travellers, or — on a tour that sells only
 *  rentals, where nobody is ever counted — the items themselves. Without the
 *  second arm a rentals-only tour could never lower its count. */
function partySize(
  categories: ReadonlyArray<PaxCategory>,
  travelers: TravelersState,
): number {
  const sellsPeople = categories.some((c) => c.chargeBase !== "asset");
  if (sellsPeople) return sumPax(categories, travelers);
  return categories.reduce((n, c) => n + (travelers[c.id] ?? 0), 0);
}

/** A party that already exceeds the picker cap, swapped for one step of the
 *  first category that fits — the preferred (default) one first, then the
 *  rest in order. The same object when the party fits, or when no category's
 *  single step fits (the package card reports that dead end).
 *
 *  The seed always puts one of the default category in the party. When that
 *  one step is bigger than the tour (a Private Group of 6 on a 2-seat tour),
 *  checkout would refuse it, and the reducer locks it in place: every "+"
 *  overshoots the cap, and the only "−" would empty the party. */
export function fitPartyToCap(args: {
  travelers: TravelersState;
  categories: ReadonlyArray<PaxCategory>;
  preferredId: string | undefined;
  pickerCap: number | null;
}): TravelersState {
  const { travelers, categories, preferredId, pickerCap } = args;
  if (pickerCap === null || sumPax(categories, travelers) <= pickerCap) {
    return travelers;
  }
  const fits = (cat: PaxCategory) => {
    const heads = paxFor(cat, 1);
    return heads > 0 && heads <= pickerCap;
  };
  const preferred = categories.find((c) => c.id === preferredId);
  const seed = preferred && fits(preferred) ? preferred : categories.find(fits);
  if (!seed) return travelers;
  return Object.fromEntries(
    categories.map((c) => [c.id, c.id === seed.id ? 1 : 0]),
  );
}

/** Counts lowered to what each rental has left, or the same object when every
 *  count already fits — so a caller can skip a state update. */
export function clampToUnitCaps(
  travelers: TravelersState,
  unitCaps: Readonly<Record<string, number>>,
): TravelersState {
  let next = travelers;
  for (const [id, cap] of Object.entries(unitCaps)) {
    if ((travelers[id] ?? 0) > cap) next = { ...next, [id]: cap };
  }
  return next;
}
